import { useTranslations } from '@vocab/react';
import { Fragment, useMemo } from 'react';

import {
  PERSONAL_DETAILS,
  SUMMARY,
  CAREER_HISTORY,
  SKILLS,
  EDUCATION,
  FINISH_RESUME,
  LANGUAGES,
  LICENCES,
} from 'src/components/Router';

import { useResumeData } from '../data';
import type { CreateResumeRoute } from '../types';

import translations from './.vocab';
import { isFieldPopulatedForResumeExport } from './utils';

interface Props {
  name: CreateResumeRoute;
  renderOnInvalid?: boolean;
  children: React.ReactNode;
}

const mapRouteToDataName: Record<string, string | string[]> = {
  [PERSONAL_DETAILS]: ['firstName', 'lastName', 'phoneNumber'],
  [SUMMARY]: 'summary',
  [CAREER_HISTORY]: 'careerHistory',
  [SKILLS]: 'skills',
  [EDUCATION]: 'education',
  [FINISH_RESUME]: [
    'firstName',
    'lastName',
    'phoneNumber',
    'summary',
    'careerHistory',
    'skills',
    'education',
  ],
  [LICENCES]: 'licences',
  [LANGUAGES]: 'languages',
};

export const useCreateResumeErrorMessage = () => {
  const { t } = useTranslations(translations);

  const errorMessages: Record<string, string> = useMemo(
    () => ({
      [PERSONAL_DETAILS]: t('Please complete all of your personal details'),
      [SUMMARY]: t('Please add a summary'),
      [CAREER_HISTORY]: t('Please add your most recent role'),
      [SKILLS]: t('Please add at least five skills'),
      [EDUCATION]: t('Please add your most recent qualification'),
      [FINISH_RESUME]: t('Please complete all other sections'),
    }),
    [t],
  );

  return errorMessages;
};

export const checkMultipleFields = (
  fieldNames: string[],
  validationResult: Record<string, boolean>,
) =>
  fieldNames
    .map((dataName: string) => validationResult[dataName] === true)
    .every((isValid: boolean) => isValid === true);

export const useIsValid = () => {
  const resumeData = useResumeData();

  return (name: CreateResumeRoute) => {
    const validationResult = isFieldPopulatedForResumeExport(resumeData);
    const dataName: string | string[] = mapRouteToDataName[name];
    return dataName && Array.isArray(dataName)
      ? checkMultipleFields(dataName, validationResult)
      : validationResult[dataName];
  };
};

export const Validation = ({
  children,
  renderOnInvalid = true,
  name,
}: Props) => {
  const checkValidity = useIsValid();
  const isValid = checkValidity(name);

  return (
    <Fragment>
      {!isValid && renderOnInvalid && children}
      {isValid && !renderOnInvalid && children}
    </Fragment>
  );
};
